import { useCallback, useContext } from "react";
import { deleteObject, listAll, ref } from "firebase/storage";
import _ from "lodash";
import { createRefPath, createRefPath_event } from "global/firestoreData/appData/appRefPaths";
import { getBaseRef } from "global/firestoreData/helpers/getBaseRef";
import { _storageSettings } from "global/storage/storageHelpers";
import { AppContext } from "../AppProvider";
import { ParentContext } from "../ParentContext";
import { gEnums } from "global/enums/globalEnums";
import { deleteField } from "firebase/firestore";

export const storageStatusTypes = {
  empty: "empty",
  deleted: "deleted",
  test: "test",
  error: "error",
};

/**
 * Deletes all items in a folder (non‑recursive subfolder deletion)
 */
const deleteStorageProfileFolder = async (mainStorage, path) => {
  const _ref = ref(mainStorage, path);

  try {
    const { items } = await listAll(_ref);
    const deletePromises = items.map((itemRef) => deleteObject(itemRef));
    await Promise.all(deletePromises);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Delete files (full + thumbnail) given a list of paths OR a map of stored files
 */
const deleteStorageFiles = async (mainStorage, allFiles, selectedFiles) => {
  const promises = [];

  if (_.isArray(selectedFiles)) {
    selectedFiles.forEach((path) => {
      const fullRef = ref(mainStorage, path);
      const thumbRef = ref(mainStorage, path + _storageSettings.thumbSuffix);
      promises.push(deleteObject(fullRef));
      promises.push(deleteObject(thumbRef));
    });
  } else {
    if (allFiles) {
      Object.keys(selectedFiles).forEach((key) => {
        const selectedFile = allFiles[key];
        const { fullPaths } = selectedFile ?? {};
        const { full, thumbnail } = fullPaths ?? {};
        if (full) promises.push(deleteObject(ref(mainStorage, full)));
        if (thumbnail) promises.push(deleteObject(ref(mainStorage, thumbnail)));
      });
    }
  }

  return Promise.all(promises);
};

/**
 * INTERNAL
 * Remove a collection of files + their database references
 */
const removePromise = async (
  mainStorage,
  storageRootPath,
  selectedImageProps,
  fileManagmentOpts,
  database_fns,
  pathViews,
) => {
  const promises = [];
  const { fileOptionType, storageType } = fileManagmentOpts

  _.forEach(selectedImageProps, (selectedImageProp, sipKey) => {
    try {
      let fileName;

      switch (fileOptionType) {
        case gEnums.fileOptionTypes.pages:
          fileName = `${fileOptionType}/${storageType}/${sipKey}/`;
          break;
        case gEnums.fileOptionTypes.global:
          fileName = `${fileOptionType}/${storageType}/${sipKey}/`;
          break;
        default:

      }

      if (fileName) {
        const fullRef = ref(mainStorage, `${storageRootPath}/${fileName}/full`);
        const thumbRef = ref(mainStorage, `${storageRootPath}/${fileName}/thumbnail`);

        promises.push(deleteObject(fullRef));
        promises.push(deleteObject(thumbRef));

        const refPath = createRefPath_event(pathViews, ['_assetUrls', fileOptionType])

        const dataToUpdate = {
          [`${storageType}.${sipKey}`]: deleteField()
        }

        // const deleteRef = createRefPath(["files", fileKey], refPath);
        const r3 = database_fns.doc_update(refPath, dataToUpdate);
        promises.push(r3);
      }
    } catch (err) {
      console.error(err);
    }
  })

  console.groupEnd()

  return Promise.all(promises);
};

/**
 * React Hook: useStorageUpdate
 * Provides delete handlers wrapped in useCallback
 */
export const useStorageUpdate = () => {

  const parentContext = useContext(ParentContext);
  const { states, fns } = parentContext ?? {}
  const { app_state, paps_state } = states ?? {}
  const { mainStorage } = app_state ?? {}
  const { database_fns } = fns
  const { pathViews } = paps_state ?? {}

  /** Delete an entire profile folder */
  const deleteFolder = useCallback(
    async (path) => {
      return deleteStorageProfileFolder(mainStorage, path);
    },
    [mainStorage]
  );

  /** Delete file(s) by paths or by referencing stored file objects */
  const deleteFiles = useCallback(
    async (allFiles, selectedFiles) => {
      return deleteStorageFiles(mainStorage, allFiles, selectedFiles);
    },
    [mainStorage]
  );

  const removeItemsFromStorage = useCallback(
    async (selectedImageProps, fileManagmentOpts) => {

      const storageRootPath = createRefPath_event(pathViews)
      return removePromise(
        mainStorage,
        storageRootPath,
        selectedImageProps,
        fileManagmentOpts,
        database_fns,
        pathViews
      );
    },
    [mainStorage, database_fns]
  );

  return {
    deleteFolder,
    deleteFiles,
    removeItemsFromStorage,
  };
};
